import { css, Global } from "@emotion/react"
import styled from "@emotion/styled"
import FocusTrap from "focus-trap-react"
import React from "react"
import { createPortal } from "react-dom"

import { breakpoints } from "../variables/layout/Breakpoints"
import { colours } from "../variables/colour/Colour"
import { horizontal, vertical } from "../variables/layout/Spacing"

interface Props {
  ariaLabelledBy?: string
  initialFocusTargetId?: string
  onClose: () => void
  className?: string
  children?: React.ReactNode
}

const CONTENT_CONTAINER_ID = "habito-modal-content-container"

const modalRef = React.createRef<HTMLDivElement>()

const ModalContainerDiv = styled.div`
  position: relative;

  // Leave layers for other components
  z-index: 10000;
`

export const ModalContainer: React.FC = () => (
  <ModalContainerDiv ref={modalRef} />
)

const Overlay = styled.div<{ modalContainerIsPresent: boolean }>`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  display: flex;
  height: 100vh;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: ${props => (props.modalContainerIsPresent ? "auto" : 10000)};

  > * {
    cursor: default;
  }
`

const nonDesktopVerticalMargin = vertical.xs

const ContentContainer = styled.div`
  background-color: ${colours.white};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  margin: ${nonDesktopVerticalMargin} ${horizontal.s};
  max-height: calc(100vh - 2 * ${nonDesktopVerticalMargin});
  outline: none;
  overflow: hidden;
  padding: ${vertical.xs} ${horizontal.s};
  position: relative;

  ${breakpoints.desktop`
    padding: ${vertical.l} ${horizontal.m};
    margin: 0;
    max-height: initial;
  `}
`

export const Modal: React.FC<Props> = ({
  ariaLabelledBy,
  children,
  initialFocusTargetId,
  onClose,
  ...props
}) => {
  const handleOverlayClick: React.MouseEventHandler<HTMLDivElement> = event => {
    // Do not hide the modal on propagated click events
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  const modalContainerElement = modalRef.current

  const modalElement = (
    <React.Fragment>
      <Global
        styles={css`
          body {
            overflow: hidden;
          }
        `}
      />

      <Overlay
        onClick={handleOverlayClick}
        modalContainerIsPresent={!!modalContainerElement}
      >
        <FocusTrap
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            initialFocus: "#" + (initialFocusTargetId || CONTENT_CONTAINER_ID),
          }}
        >
          <ContentContainer
            {...props}
            aria-labelledby={ariaLabelledBy}
            aria-modal
            id={CONTENT_CONTAINER_ID}
            role="dialog"
            tabIndex={-1}
          >
            <div
              /* Focus trap requires at least one focusable element within the
                  container, otherwise it crashes the page!
                  If you can think of a better setup, go for it.
                */
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              role="none"
            >
              {children}
            </div>
          </ContentContainer>
        </FocusTrap>
      </Overlay>
    </React.Fragment>
  )

  if (!modalContainerElement) {
    return modalElement
  }

  return createPortal(modalElement, modalContainerElement)
}
